
import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Grid, CircularProgress, Alert, ToggleButton, ToggleButtonGroup } from '@mui/material';
import CountUp from 'react-countup';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PaymentsIcon from '@mui/icons-material/Payments';
import TicketStatusBarChart from "./plots/BarPlot";
import TicketStatusDonutChart from "./plots/Dognut";
import TicketTrendChart from "./plots/TicketTrendChart";
import { ChatCenter } from "./chat/chatIconView";
import useLiveData from '../hooks/useLiveData';
import { getTicketTrendChartData, getDashboardWidgetData, getBarChartData, generateMonthWiseTrendData } from '../utils/liveDataTransformers';



export const DashboardView = ({ userCount: propUserCount, ticketCount: propTicketCount, paymentMadeCount: propPaymentMadeCount, closedCount: propClosedCount, barPlotdata: propBarPlotdata }) => {
    const { liveData, loading, error } = useLiveData(30000); // Refresh every 30 seconds
    const [widgetCounts, setWidgetCounts] = useState({
        userCount: propUserCount,
        ticketCount: propTicketCount,
        paymentMadeCount: propPaymentMadeCount,
        closedCount: propClosedCount
    });
    const [trendViewMode, setTrendViewMode] = useState('month-wise'); // 'month-wise' or 'status-wise'
    const [chartData, setChartData] = useState([
        { date: "Oct 1", tickets: 10 },
        { date: "Oct 2", tickets: 14 },
        { date: "Oct 3", tickets: 8 },
        { date: "Oct 4", tickets: 18 },
        { date: "Oct 5", tickets: 20 },
        { date: "Oct 6", tickets: 25 },
        { date: "Oct 7", tickets: 22 },
    ]);
    const [barChartData, setBarChartData] = useState(propBarPlotdata || []);

    // Update chart data when live data changes
    useEffect(() => {
        if (liveData) {
            try {
                // Transform live data for charts based on view mode
                let trendData = [];
                
                if (trendViewMode === 'month-wise') {
                    // Generate month-wise trend data
                    trendData = generateMonthWiseTrendData(liveData, 12);
                } else {
                    // Use status-wise trend data
                    trendData = getTicketTrendChartData(liveData);
                }

                const widgetData = getDashboardWidgetData(liveData);
                const barData = getBarChartData(liveData);

                // Update state with live data
                if (trendData.length > 0) {
                    setChartData(trendData);
                }
                if (barData.length > 0) {
                    setBarChartData(barData);
                }
                setWidgetCounts({
                    userCount: widgetData.userCount,
                    ticketCount: widgetData.ticketCount,
                    paymentMadeCount: widgetData.paymentMadeCount,
                    closedCount: widgetData.closedCount
                });
            } catch (err) {
                console.error('Error updating dashboard data:', err);
            }
        }
    }, [liveData, trendViewMode]);

    const widgetData = [
        {
            title: 'Total Users',
            count: widgetCounts.userCount,
            icon: <PeopleAltIcon sx={{ fontSize: 36, color: '#1976d2' }} />,
            bgColor: '#E3F2FD',
        },
        {
            title: 'Total Tickets',
            count: widgetCounts.ticketCount,
            icon: <ConfirmationNumberIcon sx={{ fontSize: 36, color: '#2e7d32' }} />,
            bgColor: '#E8F5E9',
        },
        {
            title: 'Payments Made',
            count: widgetCounts.paymentMadeCount,
            icon: <PaymentsIcon sx={{ fontSize: 36, color: '#ff6f00' }} />,
            bgColor: '#FFF3E0',
        },
        {
            title: 'Closed Tickets',
            count: widgetCounts.closedCount,
            icon: <ConfirmationNumberIcon sx={{ fontSize: 36, color: '#6a1b9a' }} />,
            bgColor: '#F3E5F5',
        }
    ];

    return (
        <Box sx={{ mb: 3 }}>
            {error && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Note: {error} - Displaying previously cached data
                </Alert>
            )}
            <Grid container spacing={2}>
                {widgetData.map((widget) => (
                    <Grid item xs={12} sm={6} md={3} key={widget.title}>
                        <Card
                            sx={{
                                borderRadius: 3,
                                boxShadow: 3,
                                backgroundColor: widget.bgColor,
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': { transform: 'scale(1.03)' },
                            }}
                        >
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {widget.icon}
                                    <Box sx={{ ml: 2, position: 'relative', width: '100%' }}>
                                        <Typography variant="body2" sx={{ color: '#555' }}>
                                            {widget.title}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                                <CountUp
                                                    start={0}
                                                    end={widget.count}
                                                    duration={2}
                                                    separator=","
                                                />
                                            </Typography>
                                            {loading && (
                                                <CircularProgress
                                                    size={20}
                                                    sx={{ ml: 1 }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            <Box sx={{ mt: 4 }}>
                {/* Trend Chart with Toggle */}
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {trendViewMode === 'month-wise' ? 'Monthly Ticket Creation Trend' : 'Ticket Status Distribution'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ToggleButtonGroup
                                    value={trendViewMode}
                                    exclusive
                                    onChange={(e, newMode) => {
                                        if (newMode !== null) {
                                            setTrendViewMode(newMode);
                                        }
                                    }}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: 1
                                    }}
                                >
                                    <ToggleButton 
                                        value="month-wise"
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.85rem',
                                            '&.Mui-selected': {
                                                backgroundColor: '#FF6B00',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: '#e55a00'
                                                }
                                            }
                                        }}
                                    >
                                        Month-wise
                                    </ToggleButton>
                                    <ToggleButton 
                                        value="status-wise"
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.85rem',
                                            '&.Mui-selected': {
                                                backgroundColor: '#FF6B00',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: '#e55a00'
                                                }
                                            }
                                        }}
                                    >
                                        Status-wise
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                {loading && <CircularProgress size={24} />}
                            </Box>
                        </Box>
                        
                        {/* Ticket Trend Chart */}
                        {chartData.length > 0 ? (
                            <TicketTrendChart data={chartData} />
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}